/**
 * AM Terminal Phase 3 — inline single-user action endpoints.
 *
 * All routes mount under /admin/account-manager/users/{userId}/...
 * Auth: AccountManagers (with managed-accounts relation) + Admins + SuperAdmins.
 * Audit: backend emits log_user_action for every action with before/after metadata.
 *
 * Phase 3b deferred: exit-position (needs orchestration/exit_service backend),
 * per-AM rate-limit, bulk variants.
 */
import apiClient from './apiClient';
import { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export interface UnsubscribeResponse {
  message: string;
}

export interface SetMultiplierResponse {
  subscription_id: string;
  lot_multiplier: number;
  previous_lot_multiplier: number;
  unchanged: boolean;
}

export interface PauseResumeResponse {
  allocation_id: string;
  status: 'ACTIVE' | 'INACTIVE';
}

function unwrapErr(error: any, fallback: string): Error {
  return new Error(
    error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback,
  );
}

class AccountManagerActionsService {
  /** Unsubscribe a managed user from a marketplace basket. Reason ≥ 10 chars required. */
  async unsubscribe(userId: string, subscriptionId: string, reason: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<UnsubscribeResponse>>(
        `/admin/account-manager/users/${userId}/subscriptions/${encodeURIComponent(subscriptionId)}`,
        { data: { reason } },
        API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to unsubscribe');
      }
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to unsubscribe');
    }
  }

  /** Edit a managed user's subscription lot_multiplier. Affects future entries only. */
  async setMultiplier(
    userId: string,
    subscriptionId: string,
    lotMultiplier: number,
  ): Promise<SetMultiplierResponse> {
    try {
      const response = await apiClient.put<ApiResponse<SetMultiplierResponse>>(
        `/admin/account-manager/users/${userId}/subscriptions/${encodeURIComponent(subscriptionId)}/multiplier`,
        { lot_multiplier: lotMultiplier },
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update multiplier');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to update multiplier');
    }
  }

  /** Pause a managed user's allocation. Stops new entries; existing positions exit normally. */
  async pauseAllocation(userId: string, allocationId: string): Promise<PauseResumeResponse> {
    try {
      const response = await apiClient.post<ApiResponse<PauseResumeResponse>>(
        `/admin/account-manager/users/${userId}/allocations/${encodeURIComponent(allocationId)}/pause`,
        {},
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to pause allocation');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to pause allocation');
    }
  }

  /** Resume a paused allocation. Refuses non-AM-paused unless force=true. */
  async resumeAllocation(
    userId: string,
    allocationId: string,
    force: boolean = false,
  ): Promise<PauseResumeResponse> {
    try {
      const url =
        `/admin/account-manager/users/${userId}/allocations/${encodeURIComponent(allocationId)}/resume`
        + (force ? '?force=true' : '');
      const response = await apiClient.post<ApiResponse<PauseResumeResponse>>(
        url, {}, undefined, API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to resume allocation');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to resume allocation');
    }
  }
}

const accountManagerActionsService = new AccountManagerActionsService();
export default accountManagerActionsService;
export { AccountManagerActionsService };
